import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";

import bhFlag from "../../../assets/images/flags/bahrain.webp";
import saFlag from "../../../assets/images/flags/ksa.webp";
import kwFlag from "../../../assets/images/flags/kuwait.webp";
import omFlag from "../../../assets/images/flags/oman.webp";
import qaFlag from "../../../assets/images/flags/qatar.webp";
import aeFlag from "../../../assets/images/flags/uae.webp";

const gccCountries = [
  { name: "Bahrain", iso2: "bh", dialCode: "973", flag: bhFlag },
  { name: "Saudi Arabia", iso2: "sa", dialCode: "966", flag: saFlag },
  { name: "United Arab Emirates", iso2: "ae", dialCode: "971", flag: aeFlag },
  { name: "Kuwait", iso2: "kw", dialCode: "965", flag: kwFlag },
  { name: "Qatar", iso2: "qa", dialCode: "974", flag: qaFlag },
  { name: "Oman", iso2: "om", dialCode: "968", flag: omFlag },
];

// The list's own cap, and the room it needs below the field before it is
// better off opening upwards.
const DROPDOWN_MAX_HEIGHT = 180;
// Breathing room between the list and the field, and between the list and the
// edge of the viewport.
const DROPDOWN_GAP = 4;
const VIEWPORT_MARGIN = 8;

const TelInput = ({
  label,
  value,
  onChange,
  error,
  name = "mobile",
  disabled = false, // new prop
  ...restProps
}) => {
  const translate = useLanguageTranslate();
  const [selected, setSelected] = useState(gccCountries[0]);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  // Where to put the list, recomputed from the trigger while it is open.
  const [menuStyle, setMenuStyle] = useState(null);

  useEffect(() => {
    if (selected?.dialCode) {
      onChange({ target: { name: "countryCode", value: selected.dialCode } });
    }
  }, [selected]);

  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    onChange({ target: { name: name, value: digits } });
  };

  // This field is the last one on several forms — the claim wizard's Your Info,
  // property damage and bodily injury pages all end with it — which puts the
  // list in the worst possible place twice over.
  //
  // It sits above the sticky footer, which is fixed to the bottom of the
  // viewport, painted white and at z-50, so a list below the field opened
  // underneath it. And the page's own `<main>` carries `overflow-x: hidden`,
  // which the spec turns into `overflow-y: auto` on the other axis — the same
  // trap the claim wizard avoids on its own container by using `clip` — making
  // main a scroll box that clips anything reaching past its bottom edge.
  // Clipping does not care about z-index, so no amount of stacking fixes it
  // while the list is inside that box.
  //
  // So the list is rendered to the body and positioned from the trigger's
  // rect: outside every ancestor that could clip it, above the footer, and
  // free to open upwards when the room below is short. It follows the trigger
  // on scroll and resize, and caps its height to the space it actually has so
  // it scrolls internally rather than running off screen.
  const placeMenu = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
    const above = rect.top - VIEWPORT_MARGIN;
    const dropUp = below < DROPDOWN_MAX_HEIGHT && above > below;
    setMenuStyle({
      position: "fixed",
      left: rect.left,
      minWidth: rect.width,
      maxHeight: Math.min(DROPDOWN_MAX_HEIGHT, Math.max(dropUp ? above : below, 96)),
      ...(dropUp
        ? { bottom: window.innerHeight - rect.top + DROPDOWN_GAP }
        : { top: rect.bottom + DROPDOWN_GAP }),
    });
  }, []);

  const toggleDropdown = () => {
    if (disabled) return;
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    placeMenu();
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    // Capture phase: the page scrolls inside `<main>`, not on the window, and
    // scroll events from a nested scroller do not bubble.
    const reposition = () => placeMenu();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [isOpen, placeMenu]);

  useEffect(() => {
    // The list is no longer inside the field, so closing on "outside" has to
    // spare it too — otherwise mousedown unmounts the option before the click
    // that chose it lands.
    const handleClickOutside = (e) => {
      if (dropdownRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="formField form-input ">
      <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px]">
        {label}
      </label>
      <div
        ref={dropdownRef}
        className={`relative flex items-center w-full border rounded-[10px] border-solid border-[#DDE2E4] h-[50px] ${
          isFocused ? "border-2 border-black" : "hover:border-[#84919A]"
        }`}
        style={{ backgroundColor: disabled ? "#E5E9EB" : "transparent" }}
        dir="ltr"
      >
        {/* Country Selector */}
        <div
          ref={triggerRef}
          className={`relative flex items-center px-4 py-2 pr-[1.75rem] pr-[1.75rem] cursor-pointer ${
            disabled ? "opacity-100 cursor-not-allowed" : ""
          }`}
          onClick={toggleDropdown}
        >
          <img
            src={selected.flag}
            alt="flag"
            className={`w-6 h-4 mr-2 rounded-sm border border-gray-300 ${
              disabled ? "opacity-50" : ""
            }`}
          />
          <span dir="ltr" className={`mr-2 ${disabled ? "text-gray-500" : ""}`}>
            +{selected.dialCode}
          </span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            } ${disabled ? "opacity-50" : ""}`}
          />
          {/* z above the sticky footer's z-50. Sized to its own content —
              "+971 — United Arab Emirates" does not fit the width of the
              flag-and-code trigger — but never narrower than the trigger, and
              capped so it stays on screen on a phone. dir is set here too: on
              an Arabic page the body is RTL and the list no longer inherits
              the field's ltr. */}
          {isOpen &&
            !disabled &&
            menuStyle &&
            createPortal(
              <div
                ref={menuRef}
                dir="ltr"
                style={menuStyle}
                className="z-[60] bg-white border rounded shadow overflow-y-auto w-max max-w-[min(18rem,75vw)]"
              >
                {gccCountries.map((c) => (
                  <div
                    key={c.iso2}
                    onClick={() => {
                      setSelected(c);
                      setIsOpen(false);
                    }}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center whitespace-nowrap"
                  >
                    <img
                      src={c.flag}
                      alt={c.name}
                      className="w-6 h-4 mr-2 rounded-sm border border-gray-300"
                    />
                    +{c.dialCode} — {c.name}
                  </div>
                ))}
              </div>,
              document.body
            )}
        </div>

        {/* Input Field */}
        <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          value={value}
          name={name}
          onChange={handleChange}
          onFocus={() => !disabled && setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className="w-full px-4 py-3 focus:outline-none border-none bg-transparent text-lg text-black"
          disabled={disabled} // Disable the input field if disabled prop is true
          style={{ backgroundColor: disabled ? "#E5E9EB" : "transparent" }}
          {...restProps}
        />
      </div>
      {error && <p className="field-err text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default TelInput;
